import {
  deriveStepInputFromSource,
  type InsertDraftStepBetweenRequest,
  type ProcessDraftStep
} from '@bliks-io/contracts';
import { resolveCurrentLaneKey } from '../../graph/resolveLane';
import { transformDraftNode } from '@/api/draftGraph';
import { swapSourceOnNode } from '../../../../../../api/mutations/helpers/draftGraphOperations';
import {
  appendPlaceholderPosition,
  buildPlaceholderStep,
  envelopePlaceholderReconcilers
} from '../../../../../../api/mutations/helpers/placeholderStep';
import { mintTempDraftStepId } from '../../../../../../api/mutations/useProcessDraft/shared';
import { insertDraftStepBetweenApi } from '../../../../../../api/persist/draft';
import { findStepByStableId } from '../shared';
import { ensureNotTerminal } from '../successorInvariant';
import type { EdgeActionDeps } from '../types';
import { ruleIdsForConnect } from './shared';

export async function insertBetweenStepAndStep(
  args: {
    sourceStepId: string;
    targetStepId: string;
    ruleId?: string;
    position: { x: number; y: number };
  },
  deps: EdgeActionDeps
): Promise<void> {
  const { sourceStepId, targetStepId, ruleId, position } = args;
  const { processId } = deps;
  const stepsBefore = deps.getCachedSteps();
  const sourceStep = findStepByStableId(stepsBefore, sourceStepId);
  // Source's derived input label toward M (for a decision: the source's title
  // + the chosen rule branch), computed once by the shared helper so the
  // optimistic label matches what the backend writes and what every other
  // surface (recording dialog, hydration) renders for the same edge.
  const derivedInput = sourceStep
    ? deriveStepInputFromSource(sourceStep, ruleId)
    : { name: '', description: '' };
  const laneHint = sourceStep
    ? resolveCurrentLaneKey(sourceStep, stepsBefore, deps.getAssigneeLookup?.())
    : undefined;
  const ruleIdsForEdge = ruleIdsForConnect(sourceStep, ruleId);

  // M's optimistic input row: previous_output referencing S with expanded
  // ruleIds, name/description pre-seeded via the shared derivation (S's title
  // + rule branch for decisions). The backend writes the canonical linked
  // input itself; this stages the same edge so the label renders instantly.
  const middleInputs: ProcessDraftStep['inputs'] = [
    {
      inputType: 'previous_output',
      name: derivedInput.name,
      description: derivedInput.description,
      sourceSteps: [
        {
          stepId: sourceStepId,
          ...(ruleIdsForEdge && ruleIdsForEdge.length > 0 ? { ruleIds: [...ruleIdsForEdge] } : {})
        }
      ]
    } as ProcessDraftStep['inputs'][number]
  ];
  const middleTask = { taskDescription: '', output: '' };

  // Optimistic-only: the insert-between endpoint clears S's ended marker
  // in-transaction (`clearEndedSuccessorStatus`), so no separate PATCH fires.
  ensureNotTerminal(sourceStepId, ruleId, deps);

  const performInsert = async (): Promise<string> => {
    const tempId = mintTempDraftStepId();
    const reconcilers = envelopePlaceholderReconcilers(tempId);
    const body: InsertDraftStepBetweenRequest = {
      source: {
        kind: 'node',
        stableStepId: sourceStepId,
        ...(ruleId ? { ruleId } : {})
      },
      targetStableStepIds: [targetStepId],
      position,
      ...(laneHint ? { laneKey: laneHint } : {})
    };

    const envelope = await deps.runTransaction({
      optimistic: (prev) => {
        const placeholder = buildPlaceholderStep({
          tempId,
          processId,
          draftId: prev.draft._id,
          inputs: middleInputs,
          task: middleTask as ProcessDraftStep['task'],
          laneKey: laneHint
        });
        // T can be a step OR a draft assignment (suggestion target) — the
        // dual-array walk swaps its S edge onto the placeholder either way.
        const rewired = transformDraftNode(prev, targetStepId, (entry) =>
          entry.kind === 'event' ? entry.node : swapSourceOnNode(entry.node, sourceStepId, tempId)
        );
        return {
          ...rewired,
          draft: { ...rewired.draft, updatedAt: new Date() },
          steps: [...rewired.steps, placeholder]
        };
      },
      optimisticLayout: (prev) => appendPlaceholderPosition(prev, tempId, position),
      // ONE transactional call: the backend creates M, swaps T's edge and
      // clears S's ended marker together — no orphan step when T turns out
      // stale (it 409s before creating anything).
      persist: () => insertDraftStepBetweenApi(processId, body),
      reconcile: reconcilers.reconcile,
      reconcileLayout: reconcilers.reconcileLayout
    });

    return String(envelope.createdNodeIds[0]);
  };

  await performInsert();
}
